/**
 * API Client for ZKCN Portal
 *
 * Handles all communication with the ZKCN Enterprise API
 */

// ============================================================================
// Types
// ============================================================================

export type CredentialType = 'AGE_VERIFICATION' | 'PERMISSIONS' | 'EDUCATION' | 'KYC_VERIFICATION' | 'IDENTITY' | 'PROFESSIONAL_LICENSE';
export type CredentialStatus = 'active' | 'expired' | 'revoked';
export type ProofType = 'CEP' | 'ARP' | 'PMP' | 'EDP' | 'KVP' | 'IDP' | 'PLP';

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  details?: unknown[];
}

export interface Credential {
  credentialId: string;
  commitmentHash: string;
  type: CredentialType;
  status: CredentialStatus;
  issuedAt: string;
  expiresAt: string | null;
  subject: {
    identifier: string;
    publicKey?: string;
  };
  verificationCount: number;
  proofData?: {
    salt: string;
    commitment: string;
  };
  transactionHash: string;
  metadata?: Record<string, string> | null;
}

export interface CredentialStats {
  totalCredentials: number;
  activeCredentials: number;
  expiredCredentials: number;
  revokedCredentials: number;
  byType: Record<CredentialType, number>;
}

export interface IssueCredentialRequest {
  type?: CredentialType;
  pluginId?: string;
  subject: {
    identifier: string;
    publicKey?: string;
  };
  claims: Record<string, unknown>;
  expiresAt?: string;
  metadata?: Record<string, string>;
}

export interface IssueCredentialResponse {
  credentialId: string;
  commitmentHash: string;
  type: CredentialType;
  issuedAt: string;
  expiresAt: string | null;
  proofData: {
    salt: string;
    commitment: string;
  };
  // Claim URL for secure delivery to subjects
  claimUrl?: string;
  claimExpiresAt?: string;
  transactionHash: string;
}

export interface VerifyProofRequest {
  commitmentHash: string;
  proof: {
    type: ProofType;
    pi_a: string[];
    pi_b: string[][];
    pi_c: string[];
    publicInputs: string[];
  };
  metadata?: {
    minAge?: number;
    maxAge?: number;
    purposeHash?: string;
  };
}

export interface VerifyProofResponse {
  isValid: boolean;
  proofType: ProofType;
  verifiedAt: number;
  recordId: number;
}

export interface AuditRecord {
  id: number;
  timestamp: string;
  commitmentHash: string;
  credentialType: CredentialType;
  verifierAddress: string;
  proofType: ProofType;
  result: boolean;
  tenantId?: string;
}

export interface ApiKey {
  id: string;
  name: string;
  keyPreview: string;
  permissions: string[];
  createdAt: string;
  lastUsed: string | null;
  status: 'active' | 'revoked';
}

export interface Webhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  createdAt: number;
  lastTriggered: number | null;
  failureCount: number;
  secret?: string;
}

export interface TeamMember {
  id: string;
  email: string;
  name?: string | null;
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';
  inviteToken?: string | null;
  invitedAt?: string | null;
  acceptedAt?: string | null;
  createdAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  email: string;
  plan: 'starter' | 'growth' | 'business' | 'enterprise';
  createdAt: string;
  apiKeys: string[];
  usage: {
    verifications: number;
    credentials: number;
    lastResetAt: string;
  };
  settings: {
    webhookUrl?: string;
    allowedOrigins: string[];
    ssoEnabled: boolean;
    notificationPreferences?: {
      emailEnabled: boolean;
      events: {
        credentialIssued: boolean;
        credentialRevoked: boolean;
        verificationCompleted: boolean;
        credentialExpiringSoon: boolean;
        credentialExpired: boolean;
      };
      usageThreshold: number | null;
      expirationWarningDays: number;
    };
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// ============================================================================
// Plugin Types
// ============================================================================

export type PluginCircuitFamily = 'ARP' | 'PMP' | 'IDP' | 'EDP' | 'KVP' | 'PLP' | 'UPC' | 'CUSTOM_NOIR';
export type PluginVisibility = 'PRIVATE' | 'PUBLIC' | 'UNLISTED';

export interface PluginClaimsField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  required: boolean;
  description?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface PluginClaimsSchema {
  fields: PluginClaimsField[];
}

export interface PluginVerificationConfig {
  minimumAge?: number;
  requestedPermissions?: number;
  ageThreshold?: number;
  nationalityFilter?: number;
  residencyCountryFilter?: number;
  residencyRegionFilter?: number;
  genderFilter?: number;
  requiredKycLevel?: number;
  providerFilter?: number;
  jurisdictionFilter?: number;
  requiredLicenseType?: number;
  specialtyFilter?: number;
  minimumDegreeLevel?: number;
  fieldOfStudyFilter?: number;
  [key: string]: unknown;
}

export interface PrivacyPlugin {
  id: string;
  tenantId: string | null;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  circuitFamily: PluginCircuitFamily;
  verificationParams: PluginVerificationConfig;
  claimsSchema: PluginClaimsSchema;
  visibility: PluginVisibility;
  isSystem: boolean;
  installCount: number;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isInstalled?: boolean;
}

export interface CreatePluginRequest {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  circuitFamily: PluginCircuitFamily;
  verificationParams?: PluginVerificationConfig;
  claimsSchema?: PluginClaimsSchema;
  visibility?: PluginVisibility;
}

export interface PluginAnalytics {
  pluginId: string;
  period: string;
  credentialsIssued: number;
  verificationsRequested: number;
  uniqueTenants: number;
  dailyBreakdown: Array<{ date: string; count: number }>;
}

// ============================================================================
// API Client Class
// ============================================================================

class ApiClient {
  private baseUrl: string;
  private apiKey: string | null = null;
  private useSessionAuth: boolean = false;

  constructor() {
    // Use relative URL for dev (Vite proxy) and production (nginx proxy)
    // Falls back to localhost:3000 only if explicitly set via env var
    this.baseUrl = import.meta.env.VITE_API_URL || '/api/v1';
    
    // Check if we have a session cookie (by attempting session validation)
    this.checkSessionAuth();
  }

  /**
   * Check if we have a valid session cookie
   */
  private async checkSessionAuth(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/session`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          this.useSessionAuth = true;
        }
      }
    } catch {
      // Session check failed — use API key auth
    }
  }

  /**
   * Set the API key for authenticated requests
   */
  setApiKey(key: string | null): void {
    this.apiKey = key;
    if (key) {
      localStorage.setItem('zkcn_api_key', key);
    } else {
      localStorage.removeItem('zkcn_api_key');
    }
  }

  /**
   * Get the current API key
   */
  getApiKey(): string | null {
    if (!this.apiKey) {
      this.apiKey = localStorage.getItem('zkcn_api_key');
    }
    return this.apiKey;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getApiKey();
  }

  /**
   * Set the base URL for the API
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
  }

  /**
   * Handle session expiration (401 responses)
   * This will be called when the server returns a 401 status
   */
  private handleSessionExpired(): void {
    // Clear the stored API key
    this.apiKey = null;
    localStorage.removeItem('zkcn_api_key');
    
    // Dispatch a custom event that components can listen to
    window.dispatchEvent(new CustomEvent('zkcn:session-expired', {
      detail: { message: 'Your session has expired. Please log in again.' }
    }));
  }

  /**
   * Make an API request
   */
  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    const apiKey = this.getApiKey();
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // Always send cookies for session auth
      });

      // Handle 401 Unauthorized - session expired
      if (response.status === 401) {
        this.handleSessionExpired();
        return {
          error: 'Session expired. Please log in again.',
        };
      }

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || `Request failed with status ${response.status}`,
          details: data.details,
        };
      }

      return { data };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // ============================================================================
  // Session Auth (httpOnly cookie-based)
  // ============================================================================

  /**
   * Login via session - exchanges API key for httpOnly cookie
   * More secure: API key is sent once and not stored in localStorage
   */
  async sessionLogin(apiKey: string): Promise<ApiResponse<{
    success: boolean;
    tenantId: string;
    tenantName: string;
    permissions: string[];
    expiresAt: string;
  }>> {
    const result = await this.request<{
      success: boolean;
      tenantId: string;
      tenantName: string;
      permissions: string[];
      expiresAt: string;
    }>('/auth/session', {
      method: 'POST',
      body: JSON.stringify({ apiKey }),
    });

    if (result.data?.success) {
      this.useSessionAuth = true;
      // Don't store the API key — session cookie handles auth
    }

    return result;
  }

  /**
   * Check current session validity
   */
  async sessionCheck(): Promise<ApiResponse<{
    authenticated: boolean;
    tenantId?: string;
    permissions?: string[];
    keyName?: string;
  }>> {
    return this.request('/auth/session');
  }

  /**
   * Logout - destroys session cookie
   */
  async sessionLogout(): Promise<ApiResponse<{ success: boolean }>> {
    const result = await this.request<{ success: boolean }>('/auth/session', {
      method: 'DELETE',
    });
    this.useSessionAuth = false;
    // Also clear any legacy API key
    this.setApiKey(null);
    return result;
  }

  /**
   * Whether we're using session-based auth
   */
  isUsingSessionAuth(): boolean {
    return this.useSessionAuth;
  }

  // ============================================================================
  // Health Endpoints
  // ============================================================================

  async health(): Promise<ApiResponse<{ status: string; version: string }>> {
    return this.request('/health');
  }

  async ready(): Promise<ApiResponse<{ ready: boolean; checks: { blockchain: boolean; database: boolean } }>> {
    return this.request('/ready');
  }

  /**
   * Get platform configuration status
   * Check if ZKCN platform is properly configured (wallet authorized, etc.)
   */
  async platformStatus(): Promise<ApiResponse<{
    configured: boolean;
    network: string;
    walletAddress: string;
    issuerAuthorized: boolean;
    contracts: {
      credentialRegistry: string;
      verificationRegistry: string;
    };
    issues: string[];
  }>> {
    return this.request('/status');
  }

  // ============================================================================
  // Auth Endpoints
  // ============================================================================

  /**
   * Get current tenant information
   */
  async me(): Promise<ApiResponse<{
    id: string;
    name: string;
    email: string;
    plan: 'starter' | 'growth' | 'business' | 'enterprise';
    createdAt: string;
    usage: {
      verifications: number;
      credentials: number;
      periodStart: string;
      periodEnd: string;
    };
    limits: {
      verifications: number;
      credentials: number;
      rateLimit: number;
    };
    apiKey: {
      preview: string;
      permissions: string[];
    };
  }>> {
    return this.request('/auth/me');
  }

  /**
   * Validate API key
   */
  async validate(): Promise<ApiResponse<{ valid: boolean; permissions: string[] }>> {
    return this.request('/auth/validate');
  }

  // ============================================================================
  // Credential Endpoints
  // ============================================================================

  async listCredentials(params?: {
    type?: CredentialType;
    status?: CredentialStatus;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<Credential>>> {
    const searchParams = new URLSearchParams();
    if (params?.type) searchParams.set('type', params.type);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return this.request(`/credentials${query ? `?${query}` : ''}`);
  }

  async getCredential(id: string): Promise<ApiResponse<Credential>> {
    return this.request(`/credentials/${id}`);
  }

  async issueCredential(data: IssueCredentialRequest): Promise<ApiResponse<IssueCredentialResponse>> {
    return this.request('/credentials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeCredential(id: string): Promise<ApiResponse<{ success: boolean; transactionHash: string }>> {
    return this.request(`/credentials/${id}`, {
      method: 'DELETE',
    });
  }

  async getCredentialStats(): Promise<ApiResponse<CredentialStats>> {
    return this.request('/credentials/stats');
  }

  async getCredentialAnalytics(days = 30): Promise<ApiResponse<{
    period: string;
    issuanceTrend: Array<{ date: string; count: number }>;
    verificationTrend: Array<{ date: string; total: number; successful: number }>;
    summary: {
      totalIssued: number;
      totalVerifications: number;
      successRate: number;
      avgIssuancePerDay: number;
    };
  }>> {
    return this.request(`/credentials/analytics?days=${days}`);
  }

  async batchCheckStatus(commitmentHashes: string[]): Promise<ApiResponse<{ results: Record<string, boolean> }>> {
    return this.request('/credentials/batch/status', {
      method: 'POST',
      body: JSON.stringify({ commitmentHashes }),
    });
  }

  // ============================================================================
  // Claim Endpoints (Public - for credential subjects)
  // ============================================================================

  /**
   * Get credential claim details (public - shows credential type/issuer, not the salt)
   */
  async getClaimDetails(token: string): Promise<ApiResponse<{
    credentialId: string;
    type: string;
    issuerName: string;
    issuedAt: string;
    expiresAt: string | null;
    claimExpiresAt: string | null;
    alreadyClaimed: boolean;
    commitmentHash: string;
  }>> {
    return this.request(`/claim/${token}`);
  }

  /**
   * Claim a credential and receive the secret salt and claims data
   * WARNING: Salt is only revealed ONCE - store it securely!
   */
  async claimCredential(token: string): Promise<ApiResponse<{
    success: boolean;
    credentialId: string;
    commitmentHash: string;
    type: string;
    salt: string;
    /** Private claims data needed for ZK proof generation */
    claims?: Record<string, unknown>;
    /** Field values as computed by Poseidon for circuit inputs */
    claimsFields?: Record<string, string>;
    /** Controller field value from issuance */
    controller?: string;
    issuerName: string;
    issuedAt: string;
    expiresAt: string | null;
  }>> {
    return this.request('/claim', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // ============================================================================
  // Verification Endpoints
  // ============================================================================

  async verifyProof(data: VerifyProofRequest): Promise<ApiResponse<VerifyProofResponse>> {
    return this.request('/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkCredentialStatus(commitmentHash: string): Promise<ApiResponse<{
    exists: boolean;
    isValid: boolean;
    isRevoked: boolean;
    isExpired: boolean;
  }>> {
    return this.request(`/verify/status/${commitmentHash}`);
  }

  async getAuditHistory(
    credentialId: string,
    params?: { page?: number; limit?: number }
  ): Promise<ApiResponse<PaginatedResponse<AuditRecord>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return this.request(`/verify/audit/${credentialId}${query ? `?${query}` : ''}`);
  }

  async listAllAuditRecords(
    params?: { page?: number; limit?: number }
  ): Promise<ApiResponse<PaginatedResponse<AuditRecord>>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return this.request(`/verify/audit${query ? `?${query}` : ''}`);
  }

  // ============================================================================
  // Tenant Endpoints
  // ============================================================================

  /**
   * Create a new tenant (public registration)
   */
  async createTenant(data: {
    name: string;
    email: string;
    plan: 'STARTER' | 'GROWTH' | 'BUSINESS' | 'ENTERPRISE';
  }): Promise<ApiResponse<{ id: string; apiKey: string }>> {
    return this.request('/tenants/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTenant(id: string): Promise<ApiResponse<Tenant>> {
    return this.request(`/tenants/${id}`);
  }

  async updateTenant(id: string, data: Partial<Tenant>): Promise<ApiResponse<Tenant>> {
    return this.request(`/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getUsage(tenantId: string): Promise<ApiResponse<Tenant['usage']>> {
    return this.request(`/tenants/${tenantId}/usage`);
  }

  // ============================================================================
  // API Key Endpoints
  // ============================================================================

  async listApiKeys(tenantId: string): Promise<ApiResponse<ApiKey[]>> {
    return this.request(`/tenants/${tenantId}/api-keys`);
  }

  async createApiKey(tenantId: string, data: {
    name: string;
    permissions: string[];
  }): Promise<ApiResponse<{ key: string; id: string }>> {
    return this.request(`/tenants/${tenantId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(tenantId: string, keyId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/tenants/${tenantId}/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Webhook Endpoints
  // ============================================================================

  async listWebhooks(): Promise<ApiResponse<{ webhooks: Webhook[] }>> {
    return this.request('/webhooks');
  }

  async createWebhook(data: {
    url: string;
    events: string[];
    secret?: string;
  }): Promise<ApiResponse<Webhook & { secret: string }>> {
    return this.request('/webhooks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWebhook(id: string): Promise<ApiResponse<Webhook>> {
    return this.request(`/webhooks/${id}`);
  }

  async updateWebhook(id: string, data: {
    url?: string;
    events?: string[];
    active?: boolean;
  }): Promise<ApiResponse<Webhook>> {
    return this.request(`/webhooks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteWebhook(id: string): Promise<ApiResponse<void>> {
    return this.request(`/webhooks/${id}`, {
      method: 'DELETE',
    });
  }

  async testWebhook(id: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return this.request(`/webhooks/${id}/test`, {
      method: 'POST',
    });
  }

  // ============================================================================
  // Verification Request Endpoints
  // ============================================================================

  /**
   * Create a verification request to send to a subject
   */
  async createVerificationRequest(data: {
    credentialType: CredentialType;
    purpose: string;
    verifierName: string;
    callbackUrl?: string;
    expiresInHours?: number;
    parameters?: {
      minAge?: number;
      maxAge?: number;
      requestedPermissions?: number;
      requiredLevel?: string;
    };
    selectiveDisclosure?: {
      requestedAttributes?: string[];
      required?: boolean;
      justification?: string;
    };
  }): Promise<ApiResponse<{
    id: string;
    token: string;
    requestUrl: string;
    qrCodeUrl: string;
    expiresAt: string;
  }>> {
    return this.request('/verification-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * List verification requests created by this tenant
   */
  async listVerificationRequests(params?: {
    status?: 'PENDING' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED' | 'FAILED';
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    items: Array<{
      id: string;
      credentialType: CredentialType;
      purpose: string;
      verifierName: string;
      status: string;
      createdAt: string;
      expiresAt: string;
      respondedAt: string | null;
      verificationResult: boolean | null;
    }>;
    total: number;
    page: number;
    pageSize: number;
  }>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    return this.request(`/verification-requests${query ? `?${query}` : ''}`);
  }

  /**
   * Get verification request details (public - for subjects)
   */
  async getVerificationRequest(token: string): Promise<ApiResponse<{
    id: string;
    credentialType: CredentialType;
    purpose: string;
    verifierName: string;
    parameters: {
      minAge?: number;
      maxAge?: number;
      requestedPermissions?: number;
      requiredLevel?: string;
    };
    status: string;
    expiresAt: string;
    isExpired: boolean;
  }>> {
    return this.request(`/verification-requests/${token}`);
  }

  /**
   * Submit proof for a verification request (public - for subjects)
   */
  async submitVerificationProof(token: string, data: {
    proof: {
      pi_a: string[];
      pi_b: string[][];
      pi_c: string[];
      publicInputs: string[];
    };
    commitmentHash: string;
  }): Promise<ApiResponse<{
    success: boolean;
    verified: boolean;
    message: string;
  }>> {
    return this.request(`/verification-requests/${token}/submit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Cancel a verification request
   */
  async cancelVerificationRequest(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/verification-requests/${id}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Subject Credential Storage Endpoints (Server-side backup)
  // ============================================================================

  /**
   * Store a claimed credential on the server (encrypted by client)
   */
  async storeClaimedCredential(
    subjectToken: string,
    data: {
      credentialId: string;
      commitmentHash: string;
      type: CredentialType;
      issuerName: string;
      issuedAt: string;
      expiresAt: string | null;
      claimsBundleEncrypted: string;
    }
  ): Promise<ApiResponse<{ success: boolean; id: string }>> {
    return this.request('/subjects/claimed-credentials', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${subjectToken}`,
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Get all claimed credentials from server (for recovery)
   */
  async getClaimedCredentials(subjectToken: string): Promise<ApiResponse<{
    credentials: Array<{
      id: string;
      credentialId: string;
      commitmentHash: string;
      type: CredentialType;
      issuerName: string;
      claimsBundleEncrypted: string;
      claimedAt: string;
      expiresAt: string | null;
      issuedAt: string;
    }>;
  }>> {
    return this.request('/subjects/claimed-credentials', {
      headers: {
        'Authorization': `Bearer ${subjectToken}`,
      },
    });
  }

  /**
   * Delete a claimed credential from server
   */
  async deleteClaimedCredential(
    subjectToken: string,
    id: string
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/subjects/claimed-credentials/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${subjectToken}`,
      },
    });
  }

  /**
   * Authenticate subject with wallet signature
   */
  async authenticateSubject(data: {
    walletAddress: string;
    signature: string;
    message: string;
  }): Promise<ApiResponse<{
    success: boolean;
    sessionToken: string;
    expiresAt: string;
  }>> {
    return this.request('/subjects/auth/wallet', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get current subject session info
   */
  async getSubjectSession(subjectToken: string): Promise<ApiResponse<{
    subjectId: string;
    walletAddress: string | null;
    email: string | null;
    expiresAt: string;
  }>> {
    return this.request('/subjects/auth/me', {
      headers: {
        'Authorization': `Bearer ${subjectToken}`,
      },
    });
  }

  // ============================================================================
  // Subject Credential Revocation (GDPR Article 17)
  // ============================================================================

  /**
   * Revoke a credential as the subject (GDPR right to erasure)
   */
  async subjectRevokeCredential(
    subjectToken: string,
    credentialId: string,
    reason?: string
  ): Promise<ApiResponse<{
    success: boolean;
    transactionHash: string | null;
    revokedAt: string;
    message: string;
  }>> {
    return this.request(`/subjects/credentials/${credentialId}/revoke`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${subjectToken}`,
      },
      body: JSON.stringify({ reason }),
    });
  }

  // ============================================================================
  // DID / Verifiable Credentials (W3C Interop)
  // ============================================================================

  /**
   * Wrap a ZKCN credential as a W3C Verifiable Credential
   */
  async exportCredentialAsVC(data: {
    credentialId: string;
    commitmentHash: string;
    type: string;
    issuedAt: string;
    expiresAt: string | null;
    issuerAddress?: string;
    subjectAddress?: string;
    network?: string;
  }): Promise<ApiResponse<{
    verifiableCredential: Record<string, unknown>;
    did: string;
  }>> {
    return this.request('/did/credentials/wrap', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Resolve a DID document
   */
  async resolveDID(did: string): Promise<ApiResponse<Record<string, unknown>>> {
    return this.request(`/did/${encodeURIComponent(did)}`);
  }

  // ============================================================================
  // Team Management
  // ============================================================================

  async listTeamMembers(): Promise<ApiResponse<{ members: TeamMember[] }>> {
    return this.request('/teams');
  }

  async inviteTeamMember(data: {
    email: string;
    name?: string;
    role?: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';
  }): Promise<ApiResponse<{ member: TeamMember; acceptUrl: string }>> {
    return this.request('/teams/invite', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTeamMemberRole(
    memberId: string,
    role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER',
  ): Promise<ApiResponse<{ member: TeamMember }>> {
    return this.request(`/teams/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeTeamMember(memberId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/teams/${memberId}`, { method: 'DELETE' });
  }

  // ============================================================================
  // Plugin Studio
  // ============================================================================

  async listPlugins(params?: {
    includeSystem?: boolean;
    includeInstalled?: boolean;
    circuitFamily?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<PrivacyPlugin>>> {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    return this.request(`/plugins${qs}`);
  }

  async getPlugin(id: string): Promise<ApiResponse<PrivacyPlugin>> {
    return this.request(`/plugins/${id}`);
  }

  async createPlugin(data: CreatePluginRequest): Promise<ApiResponse<PrivacyPlugin>> {
    return this.request('/plugins', { method: 'POST', body: JSON.stringify(data) });
  }

  async updatePlugin(id: string, data: Partial<CreatePluginRequest>): Promise<ApiResponse<PrivacyPlugin>> {
    return this.request(`/plugins/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deletePlugin(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/plugins/${id}`, { method: 'DELETE' });
  }

  async publishPlugin(id: string): Promise<ApiResponse<PrivacyPlugin>> {
    return this.request(`/plugins/${id}/publish`, { method: 'POST', body: JSON.stringify({ termsAccepted: true }) });
  }

  async getPluginAnalytics(id: string, period: '7d' | '30d' | '90d' = '30d'): Promise<ApiResponse<PluginAnalytics>> {
    return this.request(`/plugins/${id}/analytics?period=${period}`);
  }

  async listMarketplacePlugins(params?: {
    q?: string;
    circuitFamily?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<PrivacyPlugin>>> {
    const qs = params
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    return this.request(`/plugins/marketplace${qs}`);
  }

  async installPlugin(id: string): Promise<ApiResponse<{ installed: boolean; pluginId: string }>> {
    return this.request(`/plugins/marketplace/${id}/install`, { method: 'POST' });
  }

  async uninstallPlugin(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/plugins/marketplace/${id}/install`, { method: 'DELETE' });
  }
}

// Export singleton instance
export const api = new ApiClient();

// Also export class for testing
export { ApiClient };
